import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Upload, Trash2, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { API, authHeaders } from "./adminApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// If the URL starts with /api or /img — prepend backend so we display correctly.
// External http(s) URLs are used as-is.
export function resolveImageUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url; // /img/... served from frontend public
}

export default function ImagePicker({
  value,
  onChange,
  token,
  label = "Image",
  emptyLabel = "Aucune image — cliquez pour en choisir une",
}) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const fetchGallery = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/uploads`, {
        headers: authHeaders(token),
      });
      setFiles(res.data);
    } catch {
      toast.error("Impossible de charger la galerie");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchGallery();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 8 Mo)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/admin/uploads`, form, {
        headers: { ...authHeaders(token) }, // browser sets multipart boundary
      });
      toast.success("Image téléversée");
      // Auto-select the freshly uploaded image and close
      onChange(res.data.url);
      await fetchGallery();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de téléversement");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  };

  const remove = async (filename) => {
    if (!window.confirm("Supprimer cette image de la galerie ?")) return;
    try {
      await axios.delete(`${API}/admin/uploads/${filename}`, {
        headers: authHeaders(token),
      });
      setFiles((f) => f.filter((x) => x.filename !== filename));
      toast.success("Image supprimée");
    } catch {
      toast.error("Erreur");
    }
  };

  const pick = (url) => {
    onChange(url);
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <div>
      {label && (
        <label className="text-sm text-[#1A2E1C] mb-1.5 block font-medium">
          {label}
        </label>
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        data-testid="image-picker-trigger"
        className="w-full group relative rounded-2xl border-2 border-dashed border-[#E5EBE5] hover:border-[#1B5E20] hover:bg-[#F1F8F2] transition-colors p-3 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
      >
        {value ? (
          <div className="flex items-center gap-4">
            <img
              src={resolveImageUrl(value)}
              alt="aperçu"
              className="h-20 w-20 object-cover rounded-xl border border-[#E5EBE5]"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#1A2E1C]">Image sélectionnée</div>
              <div className="text-xs text-[#5C705E] truncate">{value}</div>
              <div className="text-xs text-[#1B5E20] mt-1 inline-flex items-center gap-1">
                <ImagePlus className="w-3 h-3" />
                Cliquez pour changer
              </div>
            </div>
            <button
              type="button"
              onClick={clear}
              className="p-2 rounded-full hover:bg-red-50 text-[#D32F2F]/70 hover:text-[#D32F2F]"
              title="Retirer l'image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 py-3">
            <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
              <ImagePlus className="w-6 h-6 text-[#1B5E20]" />
            </div>
            <div>
              <div className="text-sm font-medium text-[#1A2E1C]">{emptyLabel}</div>
              <div className="text-xs text-[#5C705E] mt-0.5">
                Téléversez une nouvelle image ou choisissez-en une dans votre galerie.
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          aria-describedby="image-picker-desc"
          className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl p-6 border-[#E5EBE5]"
        >
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl font-semibold text-[#1A2E1C]">
              Choisir une image
            </DialogTitle>
            <p id="image-picker-desc" className="text-sm text-[#5C705E]">
              Téléversez une nouvelle image depuis votre ordinateur, ou choisissez-en une déjà dans votre galerie.
            </p>
          </DialogHeader>

          <div className="mt-4 flex flex-wrap gap-2 justify-between items-center">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileInput}
              className="hidden"
              data-testid="image-picker-file-input"
            />
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="image-picker-upload"
              className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Téléversement…" : "Téléverser une nouvelle image"}
            </Button>
            <span className="text-xs text-[#5C705E]">
              JPG, PNG, WebP ou GIF — 8 Mo max
            </span>
          </div>

          <div className="mt-6">
            {(() => {
              if (loading) {
                return <div className="text-center py-12 text-[#5C705E]">Chargement…</div>;
              }
              if (files.length === 0) {
                return (
                  <div className="text-center py-12 border-2 border-dashed border-[#E5EBE5] rounded-2xl text-[#5C705E]">
                    Votre galerie est vide. Téléversez votre première image.
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="image-picker-gallery">
                  {files.map((f) => (
                    <div
                      key={f.filename}
                      className={`group relative rounded-2xl overflow-hidden border-2 transition cursor-pointer ${
                        value === f.url
                          ? "border-[#1B5E20] ring-2 ring-[#1B5E20]/30"
                          : "border-[#E5EBE5] hover:border-[#1B5E20]"
                      }`}
                      onClick={() => pick(f.url)}
                      data-testid={`gallery-pick-${f.filename}`}
                    >
                      <div className="aspect-square bg-[#F8FBF8]">
                        <img
                          src={resolveImageUrl(f.url)}
                          alt={f.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      {value === f.url && (
                        <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-[#1B5E20] text-white flex items-center justify-center shadow-md">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(f.filename);
                        }}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 text-[#D32F2F] flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Supprimer de la galerie"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
